/**
 * TestDefinitionFormSections.tsx
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md) — B2: the Identity,
 * Pricing, and Text sections of the one-object detail pane, plus the shared
 * section-header primitive the other sections reuse.
 *
 * Purely presentational: draft in, field changes out. All state lives in
 * TestDefinitionDetailPane; payload assembly lives in testDefinitionModel.
 */

import type { ReactNode } from 'react';
import {
  Box, InputAdornment, MenuItem, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { SCHEDULE_CATEGORY_OPTIONS } from '../../../constants/scheduleEnums';
import { TEST_UNIT_OPTIONS } from './testCatalogHelpers';
import type { DefinitionDraft } from './testDefinitionModel';

/** Lab sections — mirrors TaskTypeEditForm's picklist (single source of look). */
export const LAB_SECTION_OPTIONS = ['Rock', 'Triaxial', 'DSS-DRS', 'Oedometer', 'Ring Shear', 'Soil', 'Other'];

export type DraftFieldSetter = <K extends keyof DefinitionDraft>(
  field: K,
  value: DefinitionDraft[K],
) => void;

/** Subtle uppercase section header with an optional right-aligned action. */
export const SectionHeader = ({ title, action }: { title: string; action?: ReactNode }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pb: 0.5,
        mb: 1.5,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        }}
      >
        {title}
      </Typography>
      <Box sx={{ flex: 1 }} />
      {action}
    </Box>
  );
};

interface SectionProps {
  draft: DefinitionDraft;
  setField: DraftFieldSetter;
  /** False when the item has no primary task type (or create switch is OFF). */
  showTaskTypeFields: boolean;
}

// ── Identity ──────────────────────────────────────────────────────────────────

export const IdentitySection = ({ draft, setField, showTaskTypeFields }: SectionProps) => {
  const nameMissing = draft.TestName.trim().length === 0;
  return (
    <Box>
      <SectionHeader title="Identity" />
      <Stack spacing={1.5}>
        <TextField
          size="small"
          label="Test Name"
          required
          value={draft.TestName}
          onChange={(e) => setField('TestName', e.target.value)}
          error={nameMissing}
          helperText={
            nameMissing
              ? 'Test name is required.'
              : 'Renaming also renames the matching scheduling task type.'
          }
          inputProps={{ maxLength: 250 }}
          fullWidth
        />
        <Stack direction="row" spacing={1.5}>
          <TextField
            size="small"
            label="ASTM Code"
            value={draft.AstmCode}
            onChange={(e) => setField('AstmCode', e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="BST Code"
            value={draft.BstCode}
            onChange={(e) => setField('BstCode', e.target.value)}
            fullWidth
          />
        </Stack>
        <Stack direction="row" spacing={1.5}>
          <TextField
            select
            size="small"
            label="Category"
            value={draft.Category}
            onChange={(e) => setField('Category', e.target.value)}
            fullWidth
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {SCHEDULE_CATEGORY_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Unit"
            value={draft.Unit}
            onChange={(e) => setField('Unit', e.target.value)}
            fullWidth
          >
            {TEST_UNIT_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
        </Stack>
        {showTaskTypeFields && (
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              label="Subcategory"
              value={draft.ttSubcategory}
              onChange={(e) => setField('ttSubcategory', e.target.value)}
              inputProps={{ maxLength: 100 }}
              helperText="Task-type grouping"
              fullWidth
            />
            <TextField
              select
              size="small"
              label="Lab Section"
              value={draft.ttLabSection}
              onChange={(e) => setField('ttLabSection', e.target.value)}
              fullWidth
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {LAB_SECTION_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

// ── Pricing ───────────────────────────────────────────────────────────────────

export const PricingSection = ({ draft, setField }: Omit<SectionProps, 'showTaskTypeFields'>) => (
  <Box>
    <SectionHeader title="Pricing" />
    <Stack direction="row" spacing={1.5}>
      <TextField
        size="small"
        type="number"
        label="Unit Price (CAD)"
        value={draft.UnitPriceCad}
        onChange={(e) => setField('UnitPriceCad', e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        inputProps={{ min: 0, step: 0.01 }}
        fullWidth
      />
      <TextField
        size="small"
        type="number"
        label="Unit Price (USD)"
        value={draft.UnitPriceUsd}
        onChange={(e) => setField('UnitPriceUsd', e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        inputProps={{ min: 0, step: 0.01 }}
        helperText="Optional"
        fullWidth
      />
      <TextField
        size="small"
        type="number"
        label="Default Qty"
        value={draft.DefaultQuantity}
        onChange={(e) => setField('DefaultQuantity', e.target.value)}
        inputProps={{ min: 1 }}
        sx={{ minWidth: 110 }}
      />
    </Stack>
  </Box>
);

// ── Text ──────────────────────────────────────────────────────────────────────

export const TextSection = ({ draft, setField }: Omit<SectionProps, 'showTaskTypeFields'>) => (
  <Box>
    <SectionHeader title="Text" />
    <Stack spacing={1.5}>
      <TextField
        size="small"
        label="Description"
        value={draft.Description}
        onChange={(e) => setField('Description', e.target.value)}
        multiline
        minRows={2}
        fullWidth
      />
      <TextField
        size="small"
        label="Lab Notes"
        value={draft.LabNote}
        onChange={(e) => setField('LabNote', e.target.value)}
        multiline
        minRows={2}
        fullWidth
      />
      <TextField
        size="small"
        label="Assumptions"
        value={draft.Assumptions}
        onChange={(e) => setField('Assumptions', e.target.value)}
        multiline
        minRows={2}
        fullWidth
        helperText="Auto-appended to a quote's Assumptions when this test is added."
      />
    </Stack>
  </Box>
);
